import { RDate, makeImmutable } from "../../core";
import { Consents } from "../Consents";
import { ContactDetails, } from "../ContactDetails";
import { Delivery } from "../Delivery";
import { ApartmentInfo, DeliveryAddress, DeliveryAddressStatus, } from "../DeliveryAddress";
import { DEFAULT_FULFILLMENT_TYPE, FulfillmentInstructions, } from "../FulfillmentInstructions";
import { ASAPFulfillmentTime, FulfillmentTime, } from "../FulfillmentTime";
import { InvoiceRequest } from "../Invoice";
import { OrderReceiver } from "../OrderReceiver";
import { PurchaseOrderScope } from "../ParameterScope";
import { RelativeDatePreorderSetting, } from "../PreorderSettings";
import { ProductInstanceCreator } from "../ProductInstanceCreator";
import { PromotionScheme } from "../PromotionScheme";
import { PurchaseOrder } from "../PurchaseOrder/PurchaseOrder";
import { PurchaseOrderFulfillmentTime } from "../PurchaseOrderFulfillmentTime";
import { PurchaseOrderPayload } from "../PurchaseOrderPayload";
import { StreetAddress } from "../StreetAddress";
import { TipInstance } from "../TipInstance";
export class PurchaseOrderFactory {
    constructor(params) {
        this.country = params.country;
        this.salesChannel = params.salesChannel;
        this.validatePhoneService = params.validatePhoneService;
        this.geocodingService = params.geocodingService;
        this.isQrWaiterOrder = params.isQrWaiterOrder;
        makeImmutable(this);
    }
    createPayload(params) {
        if (params.cartStorageData === null) {
            return PurchaseOrderPayload.empty({
                menu: params.menu,
                preorderSettingsWhenEmpty: params.preorderSettingsWhenEmpty,
            });
        }
        const uninitializedPayload = PurchaseOrderPayload.fromStorageData({
            menu: params.menu,
            storageData: params.cartStorageData.purchaseOrderPayload,
            preorderSettingsWhenEmpty: params.preorderSettingsWhenEmpty,
        });
        return uninitializedPayload.mapProductInstances((productInstance) => {
            const productInstanceCreator = ProductInstanceCreator.create({
                productCategories: params.menu.productCategories,
                productTypes: params.menu.productTypes,
                productInstance,
            });
            productInstanceCreator.initWithScope(params.purchaseOrderScope);
            return productInstanceCreator.instance;
        });
    }
    create(params) {
        var _a, _b;
        const now = RDate.now(params.restaurant.timezone);
        if (params.cartStorageData !== null &&
            params.cartStorageData.restaurantId !== params.restaurant.id.value) {
            // FIXME e2e tests
            // throw new Error("Cart restaurant id deserialization mismatch");
            console.warn("Cart restaurant id deserialization mismatch");
        }
        const orderReceiver = new OrderReceiver({
            invoiceRequest: params.invoiceRequestStorageData === null
                ? null
                : InvoiceRequest.fromStorageData(params.invoiceRequestStorageData),
            contactDetails: params.contactDetailsStorageData === null
                ? ContactDetails.empty({
                    isQrWaiterOrder: this.isQrWaiterOrder,
                    validatePhoneService: this.validatePhoneService,
                    contactDetailsLock: params.contactDetailsLock,
                })
                : ContactDetails.fromStorageData({
                    validatePhoneService: this.validatePhoneService,
                    storageData: params.contactDetailsStorageData,
                    contactDetailsLock: params.contactDetailsLock,
                }),
            consents: Consents.fromStorageData(params.consentsStorageData),
        });
        const streetAddress = StreetAddress.fromStorageData({
            country: this.country,
            storageData: params.streetAddressStorageData,
        });
        const apartmentInfo = ApartmentInfo.fromStorageData({
            country: this.country,
            storageData: params.apartmentInfoStorageData,
        });
        const deliveryAddressStatus = DeliveryAddressStatus.create({
            streetAddress,
            restaurantId: params.restaurant.id,
            geocodingService: this.geocodingService,
        });
        const deliveryAddress = new DeliveryAddress({
            streetAddress,
            apartmentInfo,
            status: deliveryAddressStatus,
        });
        const fulfillmentTimeFromStorage = params.fulfillmentTimeStorageData === null
            ? null
            : FulfillmentTime.fromStorageData({
                now,
                storageData: params.fulfillmentTimeStorageData,
            });
        const fulfillmentTime = params.enableMenuFulfillmentSettings
            ? // NOTICE: if ASAP is not available (checking later in the PurchaseOrderFulfillmentTime class) then it is cleared in OnlineFulfillmentFormReactions
                fulfillmentTimeFromStorage || new ASAPFulfillmentTime()
            : fulfillmentTimeFromStorage;
        const fulfillmentMethodType = params.enableMenuFulfillmentSettings
            ? params.fulfillmentMethodTypeStorageData || DEFAULT_FULFILLMENT_TYPE
            : params.fulfillmentMethodTypeStorageData;
        const fulfillmentInstructions = FulfillmentInstructions.create({
            restaurant: params.restaurant,
            dineInLocation: params.dineInLocation,
            deliveryAddress,
            fulfillmentTime,
            fulfillmentMethodType,
            paymentPostCode: null,
        });
        const delivery = new Delivery({
            restaurantId: params.restaurant.id,
            defaultMinOrderValue: params.restaurant.minOrderValue.default,
            deliveryAddress,
        });
        const purchaseOrderScope = new PurchaseOrderScope({
            restaurant: params.restaurant,
            salesChannel: this.salesChannel,
            fulfillmentInstructions,
            now,
        });
        const preorderSettingsWhenEmpty = new RelativeDatePreorderSetting(31);
        const payload = this.createPayload({
            menu: params.restaurant.menu,
            cartStorageData: params.cartStorageData,
            purchaseOrderScope,
            preorderSettingsWhenEmpty,
        });
        const purchaseOrderFulfillmentTime = PurchaseOrderFulfillmentTime.create({
            orderingHours: params.orderingHours,
            payload,
            fulfillmentInstructions,
            enableDelayedDelivery: params.enableDelayedDelivery,
            allowOrderingInClosedRestaurant: params.allowOrderingInClosedRestaurant,
            now,
        });
        const promotionScheme = params.cartStorageData === null
            ? PromotionScheme.empty({
                inCartPromotionTypes: params.inCartPromotionTypes,
                purchaseOrderPayload: payload,
            })
            : PromotionScheme.fromStorageData({
                inCartPromotionTypes: params.inCartPromotionTypes,
                purchaseOrderPayload: payload,
                storageData: params.cartStorageData.promotionScheme,
            });
        promotionScheme.setScope(purchaseOrderScope);
        const tip = TipInstance.fromStorageData({
            areTipsEnabled: params.restaurant.tips.enabled,
            value: (_b = (_a = params.cartStorageData) === null || _a === void 0 ? void 0 : _a.tip) !== null && _b !== void 0 ? _b : null,
        });
        const purchaseOrder = new PurchaseOrder({
            restaurant: params.restaurant,
            orderingAvailability: params.orderingAvailability,
            origin: this.salesChannel,
            payload,
            orderReceiver,
            delivery,
            fulfillmentInstructions,
            purchaseOrderFulfillmentTime,
            promotionScheme,
            tip,
        });
        return { purchaseOrder, purchaseOrderScope };
    }
}
